import {
  WSAvatar,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Currency,
  Payout,
  PayoutDestinationType,
  PayoutTransferPurpose
} from "@wingspanhq/wingspan-api-sdk";
import { uniqBy } from "lodash";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { PAYOUTS_PATH } from "../..";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { PATH_TAX_WITHHOLDINGS } from "../../../TaxWithholdings/paths";
import { getDestinationName } from "../../utils/getDestinationName";
import { getPayoutNet } from "../../utils/utils";

export interface PayoutAccountsPanelProps {
  payout: Payout;
}

export const PayoutAccountsPanel: React.FC<
  PayoutAccountsPanelProps
> = props => {
  const payout = props.payout;
  const history = useHistory();
  const currency = (payout.currency ?? Currency.USD) as Currency;

  const walletTransfer = payout.transfers.find(
    transfer => transfer.purpose === PayoutTransferPurpose.WALLET_REMAINDER
  );
  const taxWithholdingTransfer = payout.transfers.find(
    transfer => transfer.purpose === PayoutTransferPurpose.TAX_WITHHOLDING
  );

  const uniqueExternalTransfers = useMemo(
    () =>
      uniqBy(
        payout.transfers.filter(
          transfer =>
            transfer.purpose === PayoutTransferPurpose.DISBURSEMENT &&
            !!transfer.destination?.resourceId
        ),
        transfer => transfer.destination?.resourceId
      ),
    [payout.transfers]
  );

  const disbursementTotal = useMemo(() => getPayoutNet(payout), [payout]);

  if (uniqueExternalTransfers.length === 0 && !walletTransfer) {
    return null;
  }

  return (
    <WSCard
      divider
      header={{
        label: "Payout accounts",
        value: {
          action: {
            label: "View details",
            onClick: () => {
              history.push(`${PAYOUTS_PATH}/${payout.id}/account-details`);
            }
          }
        }
      }}
    >
      <WSList gap="L" mb="L">
        {uniqueExternalTransfers.map(({ destination, amount }) => (
          <WSFlexBox
            wrap="nowrap"
            key={destination!.resourceId}
            justify="space-between"
            gap="M"
          >
            <WSFlexBox wrap="nowrap" alignItems="center" gap="M">
              {destination?.type === PayoutDestinationType.PAYMENT_CARD ? (
                <WSAvatar {...avatarBankCard} />
              ) : (
                <WSAvatar {...avatarBankAccount} />
              )}
              <WSText.ParagraphSm color="gray500">
                {getDestinationName(destination!)}
              </WSText.ParagraphSm>
            </WSFlexBox>
            <WSText.ParagraphSm color="gray700">
              {formatMoney(amount, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox>
        ))}

        {walletTransfer && (
          <WSFlexBox justify="space-between" gap="M">
            <WSFlexBox alignItems="center" gap="M">
              <WSAvatar {...avatarWallet} />
              <WSText.ParagraphSm>
                <a onClick={() => history.push("/member/wallet")}>
                  Wingspan Wallet
                </a>
              </WSText.ParagraphSm>
            </WSFlexBox>

            <WSText.ParagraphSm color={"gray700"}>
              {formatMoney(walletTransfer.amount, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox>
        )}

        {taxWithholdingTransfer && (
          <WSFlexBox justify="space-between" gap="M">
            <WSFlexBox alignItems="center" gap="M">
              <WSAvatar {...avatarWallet} />

              <WSText.ParagraphSm>
                <a
                  onClick={() => {
                    history.push(PATH_TAX_WITHHOLDINGS);
                  }}
                >
                  Tax Withholdings
                </a>
              </WSText.ParagraphSm>
            </WSFlexBox>

            <WSText.ParagraphSm color="gray700">
              {formatMoney(taxWithholdingTransfer.amount, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox>
        )}
      </WSList>

      <WSDivider mb="L" />
      <WSFlexBox justify="space-between">
        <WSText.ParagraphSm color={"gray500"} mr="2XL">
          Disbursement total
        </WSText.ParagraphSm>
        <WSText.ParagraphSm weight={"bold"} color={"gray700"}>
          {formatMoney(disbursementTotal, currency)}
        </WSText.ParagraphSm>
      </WSFlexBox>
    </WSCard>
  );
};
